import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { Accordion, Button, Card, Modal } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import { FieldProps } from "react-jsonschema-form";
import styled from "styled-components";
import { InnerHTML } from "../../../components/inner-html";
import { useLocaleProvider } from "../../../components/locale-provider";
import { CustomerPreference } from "../../../gen/witbooking/loyalty/customer/v1/customer_api_pb";
import { LoyaltyTag } from "../../loyaltyPreferences/domain/LoyaltyTag";
import { LoyaltyTagSection } from "../../loyaltyPreferences/domain/LoyaltyTagSection";

export interface LoyaltyPreferencesWidgetProps {
  sections: LoyaltyTagSection[];
}

interface PreferencesModalProps {
  sections: LoyaltyTagSection[];
  initialPreferences: LoyaltyTag[];
  onClose?: () => void;
  onConfirm?: (preferences: LoyaltyTag[]) => void;
}

// Section descriptions are authored in the Froala WYSIWYG editor and are stored as
// raw HTML, so they must be sanitized before being injected into the DOM
// (CWE-79 / stored XSS). `InnerHTML` applies the shared sanitization boundary.
const StyledDescription = styled(InnerHTML)`
  display: inline-flex;
  font-weight: 400;
  font-size: 14px;

  p {
    margin: 0;
  }
`;

const StyledTag = styled.span<{ selected?: boolean }>`
  border: 1px solid;
  border-radius: 0.2rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
  background-color: ${props => props.selected && "var(--secondary)"};
  color: ${props => props.selected && "white"};
`;

const messages = defineMessages({
  save: {
    id: "app.entityForm.submit",
    defaultMessage: "Save",
  },
  close: {
    id: "app.close",
    defaultMessage: "Close",
  },
  modalTitle: {
    id: "loyaltyPreferences.modalTitle",
    defaultMessage: "Customize preferences",
  },
  noPreferencesSelected: {
    id: "loyaltyPreferences.noPreferencesSelected",
    defaultMessage: "No preferences selected",
  },
  noPreferencesInSection: {
    id: "loyaltyPreferences.noPreferencesInSection",
    defaultMessage: "No preferences in section",
  },
});

function PreferencesWidget({
  schema,
  formData,
  sections,
  onChange,
}: FieldProps<CustomerPreference.AsObject[]> & LoyaltyPreferencesWidgetProps) {
  const [open, setOpen] = useState(false);
  const { currentLocale } = useLocaleProvider();

  const allPreferences = useMemo(
    () =>
      sections
        .flatMap(s => s.tags)
        .reduce((acc, tag) => ({ ...acc, [tag.ticker]: tag }), {} as Record<string, LoyaltyTag>),
    [sections]
  );

  const selectedPreferences: LoyaltyTag[] = useMemo(
    () => formData?.map(preference => allPreferences[preference.ticker]).filter(Boolean) ?? [],
    [allPreferences, formData]
  );

  const handleOnChange = useCallback(
    (preferences: LoyaltyTag[]) => {
      const customerPreferences: CustomerPreference.AsObject[] = preferences.map(preference => ({
        id: preference.id,
        ticker: preference.ticker,
      }));
      onChange(customerPreferences);
    },
    [onChange]
  );

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 items-center mb-2">
        <label className="mb-0">{schema.title}</label>
        <FontAwesomeIcon icon={faEdit} className="cursor-pointer" onClick={() => setOpen(true)} />
      </div>
      <div className="flex gap-2">
        {selectedPreferences.map(preference => (
          <span className="border rounded px-2 py-1 w-fit" key={preference.id}>
            {preference.getTranslation(currentLocale).name}
          </span>
        ))}

        {selectedPreferences.length === 0 && (
          <FormattedMessage {...messages.noPreferencesSelected} />
        )}
      </div>

      {open && (
        <PreferencesModal
          sections={sections}
          initialPreferences={selectedPreferences}
          onClose={() => setOpen(false)}
          onConfirm={handleOnChange}
        />
      )}
    </div>
  );
}

function PreferencesModal({
  sections,
  initialPreferences,
  onClose,
  onConfirm,
}: PreferencesModalProps) {
  const { currentLocale } = useLocaleProvider();
  const [selectedPreferences, setSelectedPreferences] = useState<LoyaltyTag[]>(initialPreferences);

  const isPreferenceSelected = useCallback(
    (tag: LoyaltyTag) => selectedPreferences.some(it => it.ticker === tag.ticker),
    [selectedPreferences]
  );

  const handleToggleTag = useCallback(
    (tag: LoyaltyTag) => {
      if (isPreferenceSelected(tag)) {
        setSelectedPreferences(selectedPreferences.filter(p => p.ticker !== tag.ticker));
      } else {
        setSelectedPreferences([...selectedPreferences, tag]);
      }
    },
    [isPreferenceSelected, selectedPreferences]
  );

  const handleOnConfirm = useCallback(() => {
    if (!onConfirm) return;
    onConfirm(selectedPreferences);
    onClose && onClose();
  }, [onClose, onConfirm, selectedPreferences]);

  return (
    <Modal dialogClassName="!max-w-[600px]" centered show={true} onHide={onClose} backdrop="static">
      <Modal.Header closeButton>
        <h4 className="text-center m-0">
          <FormattedMessage {...messages.modalTitle} />
        </h4>
      </Modal.Header>
      <Modal.Body>
        <Accordion defaultActiveKey="0">
          {sections.map((section, index) => (
            <div key={index}>
              <Accordion.Toggle
                className="whitespace-nowrap px-2 text-[14px]"
                as={Card.Header}
                eventKey={String(index)}>
                <span className="font-bold">{section.getTranslation(currentLocale).name}</span>
                {" - "}
                <StyledDescription html={section.getTranslation(currentLocale).description ?? ""} />
              </Accordion.Toggle>
              <Accordion.Collapse eventKey={String(index)}>
                <div className="flex flex-wrap gap-2 items-center py-3">
                  {section.tags.map(tag => (
                    <StyledTag
                      key={tag.id}
                      selected={isPreferenceSelected(tag)}
                      onClick={() => handleToggleTag(tag)}>
                      {tag.getTranslation(currentLocale).name}
                    </StyledTag>
                  ))}

                  {section.tags.length === 0 && (
                    <FormattedMessage {...messages.noPreferencesInSection} />
                  )}
                </div>
              </Accordion.Collapse>
            </div>
          ))}
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleOnConfirm}>
          <FormattedMessage {...messages.save} />
        </Button>
        <Button variant="secondary" onClick={onClose}>
          <FormattedMessage {...messages.close} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const LoyaltyPreferencesWidget = ({ sections }: LoyaltyPreferencesWidgetProps) => {
  return (props: FieldProps<CustomerPreference.AsObject[]>) => (
    <PreferencesWidget sections={sections} {...props} />
  );
};
